// Globals
import { Mentions, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { LOCALES_FIELD_NAME, useGetLocales } from '@/modules/Locales';

// Models
import { SupportedLocales } from '@/models/localization.model';

// Components
import { TagInput } from '@/ui';
import { StyledFormItem, StyledTabContainer } from '../Styled';

// Helpers
import { TFormValidatorRule } from '@/types';
import { META_TAG_PARAMETERS, MetaTagHints } from '../../helpers/constants';
import { MetaTagsFormFieldsNames } from '../../helpers/types';
import { TextEditor } from '@/components';

type TMetaTagsTabsProps = {
  hint: MetaTagHints;
  rules?: {
    [MetaTagsFormFieldsNames.TITLE]?: TFormValidatorRule[];
    [MetaTagsFormFieldsNames.DESCRIPTION]?: TFormValidatorRule[];
    [MetaTagsFormFieldsNames.KEYWORDS]?: TFormValidatorRule[];
    [MetaTagsFormFieldsNames.TEXT]?: TFormValidatorRule[];
  };
};

const MetaTagsTabs = ({ hint, rules }: TMetaTagsTabsProps) => {
  const locales = useGetLocales() ?? [];
  const { t } = useTranslation(['products']);
  const [activeTab, setActiveTab] = useState<string>(locales?.[0] || SupportedLocales.UK);
  const options = useMemo(() => META_TAG_PARAMETERS[hint], [hint]);
  return (
    <StyledTabContainer>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={Object.values(SupportedLocales).map((locale) => ({
          key: locale,
          label: locale.toUpperCase(),
          forceRender: true,
          children: (
            <>
              <StyledFormItem
                name={[LOCALES_FIELD_NAME, locale, MetaTagsFormFieldsNames.TITLE]}
                label={t('product_seo_tab_seo_title_label')}
                rules={rules?.[MetaTagsFormFieldsNames.TITLE]}
              >
                <Mentions prefix={'{{'} options={options} rows={1} />
              </StyledFormItem>
              <StyledFormItem
                name={[LOCALES_FIELD_NAME, locale, MetaTagsFormFieldsNames.DESCRIPTION]}
                label={t('product_seo_tab_seo_description_label')}
                rules={rules?.[MetaTagsFormFieldsNames.DESCRIPTION]}
              >
                <Mentions prefix={'{{'} options={options} rows={4} />
              </StyledFormItem>
              <StyledFormItem
                name={[LOCALES_FIELD_NAME, locale, MetaTagsFormFieldsNames.KEYWORDS]}
                label={t('product_seo_tab_seo_keywords_label')}
                rules={rules?.[MetaTagsFormFieldsNames.KEYWORDS]}
              >
                <TagInput
                  placeholder={t('product_seo_tab_seo_keywords_placeholder')}
                  mentionConfig={{
                    options,
                    prefix: '{{',
                    rows: 1,
                  }}
                />
              </StyledFormItem>
              <StyledFormItem
                name={[LOCALES_FIELD_NAME, locale, MetaTagsFormFieldsNames.TEXT]}
                label={t('product_seo_tab_seo_text_label')}
                rules={rules?.[MetaTagsFormFieldsNames.TEXT]}
              >
                <TextEditor />
              </StyledFormItem>
            </>
          ),
        }))}
      />
    </StyledTabContainer>
  );
};

export default MetaTagsTabs;
